import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import useCartQuote from '../hooks/useCartQuote';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Minus, Plus, Trash2, ShoppingCart, ArrowRight } from 'lucide-react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { quotedCart, packageLabel, quantityLabel, ORDERING_HOLD } from '../lib/shopping';

export function MinOrderProgress({ total, minimum }) {
  const pct = minimum > 0 ? Math.min(100, (total / minimum) * 100) : 100;
  const remaining = Math.max(0, minimum - total);
  return (
    <div data-testid="min-order-progress">
      <div className="flex justify-between gap-3 text-xs mb-1.5">
        {remaining > 0 ? (
          <>
            <span className="text-muted-foreground">
              <span className="font-mono font-semibold tabular-nums text-foreground">${remaining.toFixed(2)}</span> away from minimum
            </span>
            <span className="font-mono tabular-nums shrink-0 text-faint">${total.toFixed(2)} / ${minimum.toFixed(0)}</span>
          </>
        ) : (
          <>
            <span className="font-medium text-[hsl(var(--success))]">Minimum order met</span>
            <span className="font-mono tabular-nums shrink-0 text-faint">${total.toFixed(2)} / ${minimum.toFixed(0)}</span>
          </>
        )}
      </div>
      {/* A measured bar, not a pill: the rule reads like an instrument scale. */}
      <div className="h-1.5 bg-secondary overflow-hidden">
        <div
          className={`h-full transition-[width] duration-500 ease-out ${remaining > 0 ? 'bg-primary' : 'bg-[hsl(var(--success))]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function CartItems({ items, updateQuantity, removeItem }) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ShoppingCart className="h-9 w-9 text-faint mb-4" aria-hidden="true" />
        <p className="font-display text-lg">Your cart is empty</p>
        <p className="text-sm text-muted-foreground mt-1">Add products from the catalog.</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-border border-b border-border">
      {items.map((item) => (
        <div key={item.product_id} className="py-3.5 flex flex-col gap-2.5" data-testid="cart-drawer-item">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-base leading-snug">{item.name}</p>
              <p className="mt-0.5 font-mono text-[0.72rem] text-muted-foreground">{item.specification}</p>
              <p className="font-mono text-[0.72rem] tracking-[0.06em] text-primary">{item.sku}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono text-sm tabular-nums">{item.unavailable ? 'Not included' : item.line_total == null ? 'Awaiting quote' : `$${item.line_total.toFixed(2)}`}</p>
              <p className="font-mono text-[0.68rem] tabular-nums text-faint">{item.price == null ? '' : `$${item.price.toFixed(2)} ${packageLabel(item)}`}</p>
              {item.priceChanged && <p className="text-xs text-muted-foreground">Price updated</p>}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-11 w-11 sm:h-9 sm:w-9 p-0 shrink-0" onClick={() => updateQuantity(item.product_id, item.quantity - 1)} aria-label={`Decrease ${item.name} quantity`}>
                <Minus className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
              </Button>
              <Input type="number" min="1" value={item.quantity} onChange={(e) => updateQuantity(item.product_id, Math.max(1, parseInt(e.target.value) || 1))} className="w-16 text-center h-11 sm:h-9 font-mono tabular-nums bg-sunk border-border" aria-label={`${item.name} quantity`} />
              <Button variant="outline" size="sm" className="h-11 w-11 sm:h-9 sm:w-9 p-0 shrink-0" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} aria-label={`Increase ${item.name} quantity`}>
                <Plus className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="h-11 w-11 sm:h-9 sm:w-9 p-0 shrink-0 text-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]" onClick={() => removeItem(item.product_id)} aria-label={`Remove ${item.name} from cart`}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{quantityLabel(item)}</p>
        </div>
      ))}
    </div>
  );
}

function DrawerFooterActions({ user, total, minimum, onNavigate, pending, orderingOpen }) {
  const underMin = total < minimum;
  return (
    <div className="space-y-2.5 pt-2">
      <Button
        className="w-full h-11"
        disabled={underMin || pending || !orderingOpen || (user && user.status !== 'approved')}
        onClick={() => onNavigate(user ? '/checkout' : '/login')}
        data-testid="cart-drawer-checkout"
      >
        {!orderingOpen ? 'Ordering not open yet' : user && user.status !== 'approved' ? 'Wholesale approval required' : user ? (<>Checkout <ArrowRight className="h-4 w-4" aria-hidden="true" /></>) : 'Sign In to Checkout'}
      </Button>
      <Button variant="outline" className="w-full h-11" onClick={() => onNavigate('/cart')} data-testid="cart-drawer-view-cart">
        Review Full Cart
      </Button>
      {underMin && (
        <p className="text-xs text-center text-[hsl(var(--warning))]">
          Add <span className="font-mono tabular-nums">${(minimum - total).toFixed(2)}</span> more to meet the order minimum
        </p>
      )}
    </div>
  );
}

export default function CartDrawer() {
  const { items, updateQuantity, removeItem, drawerOpen, setDrawerOpen, storageAvailable } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = !useMediaQuery('(min-width: 768px)');
  const { quote, loading, error, retry } = useCartQuote(items, drawerOpen);

  const minimum = quote?.minimum_order ?? 300;
  const total = quote?.total ?? 0;

  const go = (path) => { setDrawerOpen(false); navigate(path); };

  const body = (
    <>
      <div className="flex-1 overflow-y-auto px-4 sm:px-5">
        {storageAvailable === false && <p role="alert" className="py-3 text-sm text-destructive">Browser storage is unavailable. Keep this tab open to preserve your cart.</p>}
        {quote?.unavailable_items?.length > 0 && <p role="alert" className="py-3 text-sm text-destructive">Unavailable products are kept for review and excluded from the total. Review your full cart before checkout.</p>}
        <CartItems items={quotedCart(items, quote)} updateQuantity={updateQuantity} removeItem={removeItem} />
      </div>
      {items.length > 0 && (
        <div className="border-t border-border bg-sunk px-4 sm:px-5 py-4 space-y-3">
          {quote && <MinOrderProgress total={total} minimum={minimum} />}
          {loading && <p role="status" className="text-xs text-muted-foreground">Updating pricing and shipping…</p>}
          {error && <div role="alert" className="text-sm text-destructive"><p>Pricing could not be refreshed. Your cart is saved.</p><Button variant="outline" className="mt-2 min-h-11" onClick={retry}>Retry pricing</Button></div>}
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="label-quiet">Total (incl. shipping)</span>
            <span className="font-mono text-lg tabular-nums" data-testid="cart-drawer-total">{quote ? `$${total.toFixed(2)}` : 'Awaiting quote'}</span>
          </div>
          {quote?.public_ordering_enabled !== true && <p className="text-xs text-muted-foreground">{ORDERING_HOLD}</p>}
          <DrawerFooterActions user={user} total={total} minimum={minimum} onNavigate={go} pending={loading || error || !quote || quote.unavailable_items?.length > 0} orderingOpen={quote?.public_ordering_enabled === true} />
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="bg-card border-border max-h-[85dvh]" data-testid="cart-drawer">
          <DrawerHeader className="text-left px-4 pt-2 pb-3">
            <DrawerTitle className="font-display text-lg">
              Cart (<span className="font-mono tabular-nums">{items.length}</span> {items.length === 1 ? 'item' : 'items'})
            </DrawerTitle>
          </DrawerHeader>
          <div className="flex flex-col min-h-0 overflow-hidden">{body}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" className="bg-card border-border w-full sm:w-[440px] max-w-[100vw] flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-4 sm:px-5 py-4 border-b border-border text-left">
          <SheetTitle className="font-display text-lg">
            Cart (<span className="font-mono tabular-nums">{items.length}</span> {items.length === 1 ? 'item' : 'items'})
          </SheetTitle>
        </SheetHeader>
        {body}
      </SheetContent>
    </Sheet>
  );
}
